import Phaser from 'phaser';
import { Player } from '../entities/Player';
import { Enemy } from '../entities/Enemy';
import { BaseWeapon, BaseProjectile } from './BaseWeapon';
import { GameConfig } from '../config/GameConfig';

export class LaserProjectile extends BaseProjectile {
  constructor(scene: Phaser.Scene, x: number, y: number, angle: number, damage: number) {
    super(scene, x, y, '');
    this.createLaserTexture(scene);
    this.setTexture('projectile-laser');
  }

  public activate(x: number, y: number, angle: number, damage: number, speed: number, lifespan: number): void {
    this.setPosition(x, y);
    this.setRotation(angle);
    this.damage = damage;

    this.setActive(true);
    this.setVisible(true);

    this.setVelocity(
      Math.cos(angle) * speed,
      Math.sin(angle) * speed
    );

    this.scene.time.delayedCall(lifespan, () => {
      if (this.active) {
        this.setActive(false);
        this.setVisible(false);
        this.setVelocity(0, 0);
      }
    });
  }

  private createLaserTexture(scene: Phaser.Scene): void {
    if (scene.textures.exists('projectile-laser')) {
      return;
    }

    const graphics = scene.add.graphics();
    const width = 20;
    const height = 4;

    // Red laser beam
    graphics.fillStyle(0xFF0000, 1);
    graphics.fillRect(0, height / 2 - 1, width, 2);

    // Bright core
    graphics.fillStyle(0xFF6666, 1);
    graphics.fillRect(0, height / 2 - 0.5, width, 1);

    // Glow effect
    graphics.fillStyle(0xFF0000, 0.3);
    graphics.fillRect(0, 0, width, height);

    graphics.generateTexture('projectile-laser', width, height);
    graphics.destroy();
  }

  public hit(): void {
    // Small flash effect
    this.scene.tweens.add({
      targets: this,
      alpha: 0,
      duration: 50,
      onComplete: () => {
        this.setActive(false);
        this.setVisible(false);
        this.setVelocity(0, 0);
      }
    });
  }
}

export class LaserPointerWeapon extends BaseWeapon {
  private currentAngle: number = 0;
  private projectileCount: number = 1;
  private attackDelay: number = GameConfig.WEAPON_DATA.LASER.COOLDOWN;

  constructor(scene: Phaser.Scene, player: Player, enemies: Phaser.Physics.Arcade.Group) {
    super(scene, player, enemies, LaserProjectile);
    this.projectiles.maxSize = GameConfig.WEAPON_DATA.LASER.MAX_PROJECTILES;
    this.startAutoAttack();

    // Pre-fill the group with inactive projectiles
    this.projectiles.createMultiple({
      classType: LaserProjectile,
      key: 'projectile-laser',
      repeat: this.projectiles.maxSize - 1,
      active: false,
      visible: false,
    });
  }

  protected startAutoAttack(): void {
    this.attackTimer = this.scene.time.addEvent({
      delay: this.attackDelay,
      callback: this.attack,
      callbackScope: this,
      loop: true
    });
  }

  protected attack(): void {
    if (!this.player.active) return;

    // Shoot projectiles in a spread pattern
    for (let i = 0; i < this.projectileCount; i++) {
      const projectile = this.projectiles.get(this.player.x, this.player.y) as LaserProjectile;
      if (projectile) {
        const angle = this.currentAngle + (i * (Math.PI * 2) / this.projectileCount);
        projectile.activate(
          this.player.x,
          this.player.y,
          angle,
          GameConfig.WEAPON_DATA.LASER.DAMAGE * this.damageMultiplier,
          GameConfig.WEAPON_DATA.LASER.PROJECTILE_SPEED,
          GameConfig.WEAPON_DATA.LASER.LIFESPAN
        );
      }
    }

    // Rotate angle for next shot
    this.currentAngle += 0.3;
  }

  public upgradeDamage(amount: number): void {
    this.damageMultiplier += amount;
  }

  public upgradeSpeed(): void {
    this.attackDelay = Math.max(300, this.attackDelay * GameConfig.WEAPON_DATA.LASER.COOLDOWN_UPGRADE);
    if (this.attackTimer) {
      this.attackTimer.destroy();
    }
    this.startAutoAttack();
  }

  public addBeam(): void {
    this.projectileCount++;
  }
}
